import { useEffect, useReducer, useRef, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useLogin, useAuth } from "@/services/userService";
import { useAuth as useKeycloakAuth } from "react-oidc-context"
import { HashLoader } from "react-spinners";
import { axiosErrorToStringError } from "@/utils/axiosErrorToStringError";
import { isAxiosError } from "axios";
import { Link } from "@/router";


const initialState = {
    username: "",
    password: "",
    error: null,
    isLoading: false
};

const reducer = (state, action) => {
    switch (action.type) {
        case "setUsername":
        return { ...state, username: action.payload, error: null };
        case "setPassword":
        return { ...state, password: action.payload, error: null };
        case "setError":
        return { ...state, error: action.payload };
        case "setIsLoading":
        return { ...state, isLoading: action.payload };
        default:
        throw new Error();
    }
};

const ACTION = {
    SET_USERNAME: "setUsername",
    SET_PASSWORD: "setPassword",
    SET_ERROR: "setError",
    SET_IS_LOADING: "setIsLoading",
}

export default function Test() {

    const firstBlock = useRef(null);
    const secondBlock = useRef(null);

    const [formState, dispatch] = useReducer(reducer, initialState);
    const [searchParams, setSearchParams] = useSearchParams();

    const { auth, setAccessToken, logoutHandler } = useAuth();
    const keycloakAuth = useKeycloakAuth()

    const login = useLogin()

    const navigate = useNavigate()

    /* On change le titre de la page */
    useEffect(() => {
        document.title = "Authentification";
    }, []);



    /* Si le paramètre noAuto est absent on redirige vers la page de connexion */
    /* Oblige a saisir le parametre noAuto=true pour se logguer avec un utilisateur local */
    useEffect(() => {
        if (!keycloakAuth.isLoading && !keycloakAuth.isAuthenticated && !searchParams.get('noAuto')) {
            keycloakAuth.signinRedirect()
        }
    }, [searchParams, keycloakAuth])


    const loginHandler = useCallback(async (event) => {

        event.preventDefault();

        if (!formState.username.trim() || !formState.password.trim()) {
            dispatch({type: ACTION.SET_ERROR, payload: "Veuillez remplir tous les champs obligatoires"});
            return;
        }

        dispatch({type: ACTION.SET_IS_LOADING, payload: true});
        dispatch({type: ACTION.SET_ERROR, payload: null});

        try {
            const response = await login(formState.username, formState.password);

            setAccessToken(response.access_token)
            navigate('/', { replace: true });
        }
        catch (error) {
            console.error("loginHandler::catch", error);
            if (error?.response && error.response?.status === 401) {
                dispatch({type: ACTION.SET_ERROR, payload: "Identifiant ou mot de passe incorrect"});
            } else if (isAxiosError(error)) {
                dispatch({type: ACTION.SET_ERROR, payload: axiosErrorToStringError(error)});
            } else {
                dispatch({type: ACTION.SET_ERROR, payload: "Erreur interne - " + error?.message || "Erreur inconnue"});
            }

            dispatch({type: ACTION.SET_IS_LOADING, payload: false});
        }

    }, [formState, login, navigate, setAccessToken]) //eslint-disable-line react-hooks/exhaustive-deps




    return (
        <div className="sm:py-8 h-screen">
            <div className="sm:mx-auto sm:w-full sm:max-w-lg sm:rounded-xl sm:border sm:border-gray-500 bg-white shadow-xl p-2 flex flex-col">
                <div>
                    <img
                        className="mx-auto h-10 w-auto"
                        src="/wsfr.png"
                        alt="Simple React App"
                    />
                    <h2 className="mt-4 text-center text-2xl font-bold leading-9 tracking-tight text-gray-9000">
                        Authentification
                    </h2>
                </div>
                <div className="mt-10">
                    { auth && ( (auth.isAuthenticated && auth.fromIdp === true) || (!auth.isAuthenticated) ) && ( 
                        <div className="border-b border-slate-200">
                            <button 
                                className="flex w-full justify-center items-center py-2 text-slate-800 font-bold"
                                onClick={() => {searchParams.set("local", false); setSearchParams(searchParams)}}
                            >
                                <span lang="fr">Se connecter avec un compte Worldskills (Victoire)</span>
                            </button>
                            <div 
                                className={`overflow-hidden transition-all duration-300 ease-in-out flex justify-center mb-3`}
                                style={
                                    ( searchParams.get('local') && searchParams.get('local') === "true" ) ? { height: 0 } : { height: "50px" }
                                }
                                ref={firstBlock}
                            >
                                {
                                    auth.isIdpLoading && (
                                        <span className="inline" lang="fr">
                                            Connexion en cours ...
                                        </span>
                                    )
                                }
                                {
                                    !auth.isIdpLoading &&!auth.isAuthenticated && (
                                        <button 
                                            className="py-2 px-2 border border-blue-500 hover:bg-blue-600 hover:text-white rounded-md" 
                                            onClick={() => keycloakAuth.signinRedirect()}
                                            lang="fr"
                                        >Se connecter</button>
                                    )
                                }
                                {
                                    auth.isAuthenticated && (
                                        <button 
                                            className="py-2 px-2 border border-red-500 hover:bg-red-600 hover:text-white rounded-md" 
                                            onClick={() => logoutHandler()}
                                            lang="fr"
                                        >Se déconnecter</button>
                                    )
                                }
                                
                            </div>
                        </div>
                    )}

                    { auth && ( ! auth.isAuthenticated || (auth.isAuthenticated && auth.fromIdp === null) ) && (
                        <div className="border-b border-slate-200">
                            <button 
                                className="flex w-full justify-center items-center py-2 text-slate-800 font-bold"
                                onClick={() => {searchParams.set("local", true); setSearchParams(searchParams)}}
                            >
                                <span lang="fr">Se connecter avec un compte local</span>
                            </button>
                            <div className={`overflow-hidden transition-all duration-300 ease-in-out`}
                                style={
                                    ( ( searchParams.get('local') && searchParams.get('local') === "true" ) || auth?.isAuthenticated ) ? { height: secondBlock?.current?.scrollHeight } : { height: 0 }
                                }
                                ref={secondBlock}
                            >
                                { !auth || !auth.isAuthenticated && (
                                    <form
                                        className="space-y-6"
                                        onSubmit={loginHandler}
                                    >
                                        <div>
                                            <label
                                                htmlFor="username"
                                                className="block text-md font-medium leading-gray-900"
                                                lang="fr"
                                            >
                                                Utilisateur <span className="text-red-600">*</span>
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    type="text"
                                                    id="username"
                                                    name="username"
                                                    required
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                    value={formState.username}
                                                    onChange={e => dispatch({type: ACTION.SET_USERNAME, payload: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label
                                                htmlFor="password"
                                                className="block text-md font-medium leading-gray-900"
                                            >
                                                Mot de passe <span className="text-red-600">*</span>
                                            </label>
                                            <div className="mt-2">
                                                <input
                                                    type="password"
                                                    id="password"
                                                    name="password"
                                                    required
                                                    className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                                                    value={formState.password}
                                                    onChange={e => dispatch({type: ACTION.SET_PASSWORD, payload: e.target.value})}
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <button
                                                type="submit"
                                                disabled={formState.isLoading}
                                                onClick={loginHandler}
                                                className={`flex w-full justify-center rounded-md ${
                                                formState.isLoading ? "bg-indigo-200" : "bg-indigo-600"
                                                } px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm ${
                                                !formState.isLoading &&
                                                "hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                                                }`}
                                            >
                                                {formState.isLoading ? (
                                                <span className="inline" lang="fr">
                                                    <HashLoader
                                                    size={20}
                                                    loading={formState.isLoading}
                                                    cssOverride={{ display: "inline", marginRight: "18px" }}
                                                    color="white"
                                                    />
                                                    Connexion en cours
                                                </span>
                                                ) : (
                                                "Se connecter"
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                )}
                                { auth && auth.isAuthenticated && (
                                    <div className="flex flex-row gap-2 items-center justify-center mb-5">
                                        <Link
                                            to="/logout" 
                                            className="py-2 px-2 border border-red-500 hover:bg-red-600 hover:text-white rounded-md" 
                                            onClick={() => logoutHandler()}
                                            lang="fr"
                                        >Se déconnecter</Link>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                    {formState.error && (
                        <span className="block rounded-xl text-center font-bold my-4 mx-4 border border-red-500 text-red-800">
                        {formState.error}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
}
